import React, { useCallback, useContext, useMemo } from 'react';

import { AuthContext, type AuthContextValue } from '~/auth';
import dayjs from '~/timeInit';

import { Box, Container, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  ROLE_ADMIN_USER,
  ROLE_EMPLOYEE_USER,
  ROLE_EXPERIENCES_COORDINATOR,
  ROLE_ICS_STORE_TEAM,
  ROLE_TOUR_COORDINATOR,
} from '~/consts/roles';

import useAppNavigation from '~/hooks/useAppNavigation';

import { TryBetaButton } from './Common/TryBetaButton';
import UserSearchWidget, { SearchResult } from './Common/UserSearchWidget';
import RecentlyViewedProducts from './Products/RecentlyViewedProducts';
import RecentlyViewedPurchases from './Purchases/RecentlyViewedPurchases';
import RecentUsers from './Users/RecentUsers';
import { withTenant } from './hoc';

function IndexPage() {
  const { user } = useContext<AuthContextValue>(AuthContext);
  const { push } = useAppNavigation();
  const handleWidgetChange = useCallback(() => {}, []);

  const canSearchUsers = useMemo(() => {
    if (!user?.roles) {
      return false;
    }

    const allowedRoles = [
      ROLE_ADMIN_USER,
      ROLE_EMPLOYEE_USER,
      ROLE_EXPERIENCES_COORDINATOR,
      ROLE_ICS_STORE_TEAM,
      ROLE_TOUR_COORDINATOR,
    ];

    return allowedRoles.some((role) => user.roles.includes(role));
  }, [user]);

  const handleSearchSelect = useCallback(
    (result: SearchResult | null) => {
      if (!result) {
        return;
      }

      let pathname: string;
      if (result.entityType === 'order') {
        pathname = `/purchases/${result.value}`;
      } else if (result.entityType === 'offer') {
        pathname = result.targetPath ?? `/offers/${result.value}`;
      } else {
        pathname = `/users/${result.value}`;
      }
      push({ pathname });
    },
    [push],
  );

  const showLastLogin = useMemo(() => {
    if (user) {
      const lastLogin = user.lastLogin;
      if (lastLogin.login_date) {
        const lastLoginDate = new Date(lastLogin.login_date);
        return (
          <Box>
            You last logged in {dayjs(lastLoginDate).fromNow()} on {dayjs(lastLoginDate).calendar()}{' '}
          </Box>
        );
      }
    }
  }, [user]);

  return (
    <Container maxWidth="xl">
      <Paper className="user-panel">
        <Typography fontWeight="bold" variant="h3">
          Welcome {user ? ' ' + user.givenName : null}!
        </Typography>
        {showLastLogin}
        <TryBetaButton />
      </Paper>
      {canSearchUsers && (
        <Box mt={4}>
          <UserSearchWidget
            id="fk_customer_id"
            label="Search for a customer, Société account, offer or order"
            formErrors={[]}
            loading={false}
            onChange={handleWidgetChange}
            includeOrders
            includeOffers
            onSelectResult={handleSearchSelect}
          />
        </Box>
      )}
      <Grid container spacing={2} paddingTop={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <RecentUsers />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <RecentlyViewedPurchases />
          <Box mt={4}>
            <RecentlyViewedProducts />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}

export default withTenant(IndexPage);
